import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, X } from 'lucide-react';
import { Button } from './ui/button';

const STORAGE_KEY = 'idk-cookie-consent';

export const CookieConsentBanner = () => {
  // `mounted` guarantees the banner is completely absent from the initial
  // server-rendered / pre-rendered HTML. Only after client-side mount do we
  // read localStorage and decide whether to reveal it. This eliminates the
  // brief flash of the banner that pre-rendered snapshots would otherwise
  // cause on returning visitors who already dismissed it.
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setMounted(true);
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return; // returning visitor already made a choice
    const timer = setTimeout(() => setVisible(true), 800);
    return () => clearTimeout(timer);
  }, []);

  const handleChoice = (choice) => {
    localStorage.setItem(STORAGE_KEY, choice);
    localStorage.setItem(`${STORAGE_KEY}-at`, new Date().toISOString());
    setVisible(false);
  };

  if (!mounted || !visible) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Kuki razılıq bildirişi"
      data-testid="cookie-consent-banner"
      className="fixed bottom-0 inset-x-0 z-[60] px-4 pb-4 sm:pb-6 pointer-events-none animate-in slide-in-from-bottom duration-500"
    >
      <div className="mx-auto max-w-3xl pointer-events-auto rounded-2xl border border-gray-200 bg-white shadow-2xl overflow-hidden">
        <div className="p-5 sm:p-6">
          <div className="flex items-start gap-4">
            <div className="hidden sm:flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-blue-50">
              <Cookie className="h-5 w-5 text-blue-700" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-3">
                <h3 className="text-base font-semibold text-gray-900">
                  Kuki fayllarından istifadə
                </h3>
                <button
                  type="button"
                  onClick={() => handleChoice('dismissed')}
                  aria-label="Bağla"
                  className="shrink-0 -mt-1 -mr-1 p-1 text-gray-400 hover:text-gray-600 transition-colors"
                  data-testid="cookie-close-btn"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <p className="mt-1.5 text-sm text-gray-600 leading-relaxed">
                Bu sayt təcrübənizi yaxşılaşdırmaq və əsas funksiyaların işini təmin etmək üçün
                zəruri kuki fayllarından istifadə edir. Ətraflı məlumat üçün{' '}
                <Link
                  to="/mexfilik-siyaseti"
                  className="font-medium text-blue-700 hover:text-blue-800 underline underline-offset-2"
                  data-testid="cookie-privacy-link"
                >
                  Məxfilik siyasətimizə
                </Link>{' '}
                baxa bilərsiniz.
              </p>
              <div className="mt-4 flex flex-col-reverse sm:flex-row gap-2 sm:gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleChoice('rejected')}
                  className="sm:min-w-[110px]"
                  data-testid="cookie-reject-btn"
                >
                  İmtina edirəm
                </Button>
                <Button
                  size="sm"
                  onClick={() => handleChoice('accepted')}
                  className="bg-blue-700 hover:bg-blue-800 text-white sm:min-w-[110px]"
                  data-testid="cookie-accept-btn"
                >
                  Qəbul edirəm
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
